import { useEffect, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

export function AnimatedBackground({ variant = 'default' }) {
  const { isDarkMode } = useTheme();
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let particles = [];
    let connections = [];
    
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    resize();
    window.addEventListener('resize', resize);
    
    // Initialize particles
    const particleCount = variant === 'dense' ? 80 : 50;
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        radius: Math.random() * 2 + 1,
        color: isDarkMode 
          ? `rgba(99, 102, 241, ${Math.random() * 0.3 + 0.1})`
          : `rgba(99, 102, 241, ${Math.random() * 0.2 + 0.05})`,
      });
    }
    
    // Data stream particles (for cyber effect)
    const dataStreams = [];
    if (variant === 'cyber') {
      for (let i = 0; i < 20; i++) {
        dataStreams.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          speed: Math.random() * 2 + 1,
          length: Math.random() * 50 + 20,
          opacity: Math.random() * 0.3 + 0.1,
        });
      }
    }
    
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw connections
      particles.forEach((p1, i) => {
        particles.slice(i + 1).forEach(p2 => {
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < 150) {
            const opacity = (1 - distance / 150) * (isDarkMode ? 0.15 : 0.08);
            ctx.beginPath();
            ctx.strokeStyle = isDarkMode 
              ? `rgba(99, 102, 241, ${opacity})`
              : `rgba(99, 102, 241, ${opacity})`;
            ctx.lineWidth = 1;
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        });
      });
      
      // Update and draw particles
      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        
        // Bounce off edges
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.fill();
      });
      
      // Draw data streams (cyber variant)
      if (variant === 'cyber') {
        dataStreams.forEach(stream => {
          stream.y += stream.speed;
          if (stream.y > canvas.height + stream.length) {
            stream.y = -stream.length;
            stream.x = Math.random() * canvas.width;
          }
          
          const gradient = ctx.createLinearGradient(
            stream.x, stream.y - stream.length,
            stream.x, stream.y
          );
          gradient.addColorStop(0, 'transparent');
          gradient.addColorStop(1, isDarkMode 
            ? `rgba(34, 211, 238, ${stream.opacity})`
            : `rgba(6, 182, 212, ${stream.opacity * 0.5})`
          );
          
          ctx.beginPath();
          ctx.strokeStyle = gradient;
          ctx.lineWidth = 1;
          ctx.moveTo(stream.x, stream.y - stream.length);
          ctx.lineTo(stream.x, stream.y);
          ctx.stroke();
        });
      }
      
      animationFrameId = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [isDarkMode, variant]);
  
  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-0 pointer-events-none"
      style={{ opacity: isDarkMode ? 0.6 : 0.4 }}
    />
  );
}

export function GlowOrbs() {
  const { isDarkMode } = useTheme();
  
  return (
    <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
      {/* Primary orb */}
      <div 
        className={`absolute w-96 h-96 rounded-full blur-3xl animate-float ${
          isDarkMode ? 'bg-indigo-600/10' : 'bg-indigo-300/20'
        }`}
        style={{ 
          top: '10%', 
          left: '15%',
          animation: 'float 8s ease-in-out infinite'
        }}
      />
      {/* Secondary orb */}
      <div 
        className={`absolute w-80 h-80 rounded-full blur-3xl ${
          isDarkMode ? 'bg-violet-600/10' : 'bg-violet-300/20'
        }`}
        style={{ 
          bottom: '20%', 
          right: '10%',
          animation: 'float 10s ease-in-out infinite',
          animationDelay: '2s'
        }}
      />
      {/* Tertiary orb */}
      <div 
        className={`absolute w-64 h-64 rounded-full blur-3xl ${
          isDarkMode ? 'bg-cyan-600/5' : 'bg-cyan-300/15'
        }`}
        style={{ 
          top: '50%', 
          left: '50%',
          transform: 'translate(-50%, -50%)',
          animation: 'pulse 6s ease-in-out infinite'
        }}
      />
    </div>
  );
}

export function GridPattern() {
  const { isDarkMode } = useTheme();
  
  return (
    <div 
      className="fixed inset-0 z-0 pointer-events-none"
      style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, ${
          isDarkMode ? 'rgba(99, 102, 241, 0.15)' : 'rgba(99, 102, 241, 0.1)'
        } 1px, transparent 0)`,
        backgroundSize: '40px 40px',
        opacity: isDarkMode ? 0.5 : 0.4
      }}
    />
  );
}
